import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IPersonaProps } from '@fluentui/react';
import { Field, FluentProvider, Textarea, webLightTheme } from '@fluentui/react-components';
import React from 'react';
import { Controller, FieldValues, UseFormMethods } from 'react-hook-form';
import ErrorHandlingPeoplePicker from '../../common/formFields/ErrorHandlingPeoplePicker';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';

export interface IContactsInformation {
  terminationContacts?: IServiceContact[];
  celaTermContact?: IServiceContact;
  businessStakeholder?: IServiceContact;
  statutoryController?: IServiceContact;
  generalComments?: string;
}

interface IEntityTerminationContactsEditFormProps {
  form: UseFormMethods<FieldValues>;
  contactsInfo?: IContactsInformation;
}

const EntityTerminationContactsEditForm = (
  props: IEntityTerminationContactsEditFormProps,
): JSX.Element => {
  const { form, contactsInfo } = props;
  const { control, setValue, errors } = form;

  const onResolveSuggestions = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return searchForUsers(filterText);
    }
    return [];
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Controller
          name="terminationContacts"
          control={control}
          defaultValue={contactsInfo?.terminationContacts ?? []}
          render={({ value }) => (
            <ErrorHandlingPeoplePicker
              controlName="terminationContacts"
              labelText="Termination Contacts"
              onChange={(items: IPersonaProps[]) => {
                setValue('terminationContacts', items, { shouldDirty: true });
              }}
              removeButtonAriaLabel="Remove Termination Contacts"
              onResolveSuggestions={onResolveSuggestions}
              selectedItems={value || []}
              errorMessage={errors?.terminationContacts?.message}
            />
          )}
        />
        <Controller
          name="celaTermContact"
          control={control}
          defaultValue={contactsInfo?.celaTermContact ?? null}
          render={({ value }) => (
            <ErrorHandlingPeoplePicker
              controlName="celaTermContact"
              labelText="CELA Termination Contact"
              onChange={(items: IPersonaProps[]) => {
                setValue('celaTermContact', items[0] ?? null, { shouldDirty: true });
              }}
              itemLimit={1}
              selectedItems={value != null ? [value] : []}
              onResolveSuggestions={onResolveSuggestions}
              errorMessage={errors?.celaTermContact?.message}
            />
          )}
        />
        <Controller
          name="businessStakeholder"
          control={control}
          defaultValue={contactsInfo?.businessStakeholder ?? null}
          render={({ value }) => (
            <ErrorHandlingPeoplePicker
              controlName="businessStakeholder"
              labelText="Business Stakeholder"
              itemLimit={1}
              onResolveSuggestions={onResolveSuggestions}
              removeButtonAriaLabel="Remove Business Stakeholder"
              selectedItems={value ? [value] : []}
              onChange={(items: IPersonaProps[]) => {
                setValue('businessStakeholder', items[0] ?? null, { shouldDirty: true });
              }}
              errorMessage={errors?.businessStakeholder?.message}
            />
          )}
        />
        <Controller
          name="statutoryController"
          control={control}
          defaultValue={contactsInfo?.statutoryController ?? null}
          render={({ value }) => (
            <ErrorHandlingPeoplePicker
              controlName="statutoryController"
              labelText="Statutory Controller"
              itemLimit={1}
              removeButtonAriaLabel="Remove Statutory Controller"
              selectedItems={value ? [value] : []}
              disabled
              errorMessage={errors?.statutoryController?.message}
            />
          )}
        />
        <Controller
          name="generalComments"
          control={control}
          defaultValue={contactsInfo?.generalComments || ''}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="General Comments" validationMessage={errors?.generalComments?.message}>
              <Textarea
                id="generalComments"
                name={name}
                value={value}
                onChange={(e, textData): void => {
                  onChange(textData.value);
                }}
                resize="vertical"
              />
            </Field>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default EntityTerminationContactsEditForm;
